import { isPlainObject } from 'lodash';

/**
 * Deep clones an object for safe assignment to MST stores.
 * MST doesn't allow the same object instance to be used in multiple places,
 * so we need to ensure all nested objects are cloned.
 */
export function deepCloneForMST<T>(obj: T): T {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (obj instanceof Date) {
    return new Date(obj.getTime()) as any;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => deepCloneForMST(item)) as any;
  }

  if (typeof obj === 'object') {
    const cloned: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        cloned[key] = deepCloneForMST(obj[key]);
      }
    }
    return cloned;
  }

  // Primitive values (string, number, boolean) can be returned as-is
  return obj;
}

/**
 * Safely merges updates into an MST node, ensuring all objects are cloned.
 * This prevents the "object already part of state tree" error.
 */
export function safeMergeToMST<T extends Record<string, any>>(
  target: T,
  updates: Partial<T>
): void {
  for (const key in updates) {
    if (updates.hasOwnProperty(key)) {
      const value = updates[key];
      if (value !== undefined) {
        // Clone objects and arrays, but keep primitives as-is
        (target as any)[key] = deepCloneForMST(value);
      }
    }
  }
}

/**
 * Creates a safe clone of an MST node for backup/rollback purposes.
 */
export function cloneMSTNode<T extends Record<string, any>>(node: T): T {
  return deepCloneForMST(node);
}

/**
 * Safely assigns an external object (e.g., from API response) to MST.
 * Ensures all nested objects are cloned to prevent reference issues.
 */
export function fromAPIToMST<T>(apiResponse: T): T {
  return deepCloneForMST(apiResponse);
}

/**
 * Type guard to check if a value is a plain object (not a class instance, Date, etc.)
 */
export function isPlainObjectStrict(value: any): boolean {
  if (!value || typeof value !== 'object') {
    return false;
  }
  
  // Check if it's a plain object (not Date, Array, etc.)
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}
